import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert,
  Paper, Divider,
} from '@mui/material';
import PaymentIcon      from '@mui/icons-material/Payment';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import LockIcon         from '@mui/icons-material/Lock';
import { iniciarPagoInscripcion, getEstadoPagoInscripcion } from '../services/ecolabApi';

export default function PagoInscripcionWidget({ afiliadoId, nombres = '', onPagado }) {
  const wompiRef   = useRef(null);
  const [fase,     setFase]     = useState('idle'); // idle | iniciando | widget | pagado
  const [wompiData,setWompiData]= useState(null);
  const [error,    setError]    = useState('');
  const [checking, setChecking] = useState(true);

  // Verificar si ya pagó al montar
  useEffect(() => {
    getEstadoPagoInscripcion(afiliadoId)
      .then(d => { if (d.confirmado) { setFase('pagado'); onPagado?.(); } })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [afiliadoId, onPagado]);

  // Montar script Wompi cuando tengamos datos Y el ref esté disponible
  useEffect(() => {
    if (!wompiData) return;

    // Esperar a que React haya renderizado el contenedor
    const timer = setTimeout(() => {
      const c = wompiRef.current;
      if (!c) return;

      const key = wompiData.publicKey || '';
      if (!key || key.includes('XXXX') || key.length < 10) {
        setError('La pasarela de pagos no está configurada. Contacte al administrador.');
        setFase('idle'); return;
      }

      c.innerHTML = '';
      const script = document.createElement('script');
      script.src = 'https://checkout.wompi.co/widget.js';
      script.setAttribute('data-render',             'button');
      script.setAttribute('data-public-key',          key);
      script.setAttribute('data-currency',            'COP');
      script.setAttribute('data-amount-in-cents',     String(wompiData.amountInCents));
      script.setAttribute('data-reference',           wompiData.referencia);
      script.setAttribute('data-signature:integrity', wompiData.integrityHash);
      script.setAttribute('data-redirect-url',        wompiData.redirectUrl);
      if (wompiData.email)
        script.setAttribute('data-customer-data:email', wompiData.email);
      if (wompiData.nombreCompleto)
        script.setAttribute('data-customer-data:full-name', wompiData.nombreCompleto);
      script.onload  = () => setFase('widget');
      script.onerror = () => {
        setError('No se pudo conectar con Wompi. Intente de nuevo.');
        setFase('idle');
      };
      c.appendChild(script);
    }, 100); // pequeño delay para garantizar que el DOM esté listo

    return () => clearTimeout(timer);
  }, [wompiData]);

  const iniciar = async () => {
    setFase('iniciando'); setError('');
    try {
      const data = await iniciarPagoInscripcion(afiliadoId);
      setWompiData(data);
    } catch(e) {
      setError(e?.data?.mensaje || 'Error al iniciar el pago. Intente nuevamente.');
      setFase('idle');
    }
  };

  if (checking) return <CircularProgress size={24}/>;

  if (fase === 'pagado') return (
    <Alert severity="success" icon={<CheckCircleIcon/>} sx={{ borderRadius:2 }}>
      <strong>¡Pago de inscripción confirmado!</strong> Ya puede agendar su sesión de orientación.
    </Alert>
  );

  return (
    <Paper elevation={0} sx={{ border:'2px solid #e65100', borderRadius:3, overflow:'hidden' }}>
      {/* Header */}
      <Box sx={{ background:'linear-gradient(135deg,#e65100,#ff8f00)', px:3, py:2,
        display:'flex', alignItems:'center', gap:1.5 }}>
        <PaymentIcon sx={{ color:'#fff', fontSize:28 }}/>
        <Box>
          <Typography sx={{ color:'#fff', fontWeight:700, fontSize:'1rem' }}>
            Pago de Inscripción Requerido
          </Typography>
          <Typography sx={{ color:'rgba(255,255,255,0.85)', fontSize:'0.78rem' }}>
            Antes de agendar su sesión de orientación
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p:3 }}>
        {/* Detalle del cobro */}
        <Box sx={{ mb:2.5 }}>
          <Box sx={{ display:'flex', justifyContent:'space-between', py:1 }}>
            <Typography variant="body2" color="text.secondary">Afiliación a la cooperativa</Typography>
            <Typography variant="body2" fontWeight={600}>$60.000 COP</Typography>
          </Box>
          <Divider/>
          <Box sx={{ display:'flex', justifyContent:'space-between', py:1 }}>
            <Typography variant="body2" color="text.secondary">Afiliación red de Consumo Colaborativo</Typography>
            <Typography variant="body2" fontWeight={600}>$60.000 COP</Typography>
          </Box>
          <Divider/>
          <Box sx={{ display:'flex', justifyContent:'space-between', py:1.5,
            background:'#fff8e1', borderRadius:1, px:1, mt:1 }}>
            <Typography variant="body2" fontWeight={700}>Total a pagar</Typography>
            <Typography variant="h6" fontWeight={800} color="#e65100">$120.000 COP</Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb:2 }} onClose={()=>setError('')}>{error}</Alert>}

        {/* Botón iniciar — visible solo en idle */}
        {fase === 'idle' && (
          <Button fullWidth variant="contained" size="large" onClick={iniciar}
            startIcon={<PaymentIcon/>}
            sx={{ background:'linear-gradient(135deg,#e65100,#ff8f00)', fontWeight:700,
              borderRadius:2, py:1.5 }}>
            Pagar $120.000 COP con Wompi
          </Button>
        )}

        {/* Spinner mientras inicia */}
        {fase === 'iniciando' && (
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:1.5, py:2 }}>
            <CircularProgress size={20} sx={{ color:'#e65100' }}/>
            <Typography variant="body2" color="text.secondary">Preparando pasarela de pago…</Typography>
          </Box>
        )}

        {/* Contenedor Wompi — SIEMPRE MONTADO cuando wompiData existe */}
        {wompiData && (
          <Box>
            {fase === 'iniciando' && (
              <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
                <CircularProgress size={14}/>
                <Typography variant="caption" color="text.secondary">Cargando pasarela…</Typography>
              </Box>
            )}
            <Box ref={wompiRef}/>
            {fase === 'widget' && (
              <Button size="small" color="inherit" sx={{ mt:1, color:'#aaa', fontSize:'0.72rem' }}
                onClick={() => { setWompiData(null); setFase('idle'); }}>
                Cancelar
              </Button>
            )}
          </Box>
        )}

        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:0.5, mt:2 }}>
          <LockIcon sx={{ fontSize:14, color:'#aaa' }}/>
          <Typography variant="caption" color="text.disabled">
            Pago seguro procesado por Wompi · Tus datos están protegidos
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
